import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useBulkImport } from '../../hooks/useBulkImport';
import AdminBreadcrumb from '../../components/admin/AdminBreadcrumb';
import SafeIcon from '../../common/SafeIcon';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiDownload, FiCheck, FiX, FiAlertCircle } = FiIcons;

const BulkImport = () => {
  const { courseId } = useParams();
  const { importLessons, loading, progress, results } = useBulkImport();
  const [dragOver, setDragOver] = useState(false);

  const breadcrumbItems = [
    { label: 'Admin', href: '/admin' },
    { label: 'Courses', href: '/admin/courses' },
    { label: 'Bulk Import', href: '#' }
  ];

  const handleFileUpload = async (file) => {
    if (file && file.type === 'text/csv') {
      await importLessons(courseId, file);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    handleFileUpload(file);
  };

  const downloadTemplate = () => {
    const csvContent = `module,lessonTitle,videoUrl,textContent,estimatedMinutes,resources
"Introduction to Nutrition","What is Nutrition?","https://youtube.com/watch?v=example","# What is Nutrition?

Nutrition is the science that studies how food affects our health...",15,"[]"
"Introduction to Nutrition","Macronutrients Overview","https://youtube.com/watch?v=example2","## Macronutrients

The three main macronutrients are:
- Carbohydrates
- Proteins
- Fats",20,"[{""label"": ""Macronutrients Guide"", ""file_url"": ""https://example.com/guide.pdf"", ""description"": ""Comprehensive guide to macronutrients""}]"
"Micronutrients","Vitamins and Minerals","","### Essential Vitamins

Vitamins are organic compounds that are essential for normal growth...",10,"[]"`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lesson-import-template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AdminBreadcrumb items={breadcrumbItems} />

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Bulk Import Lessons</h1>
          <p className="text-gray-600 mt-2">Import multiple lessons from a CSV file</p>
        </div>

        {/* Instructions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8"
        >
          <div className="flex items-start space-x-3">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900 mb-2">Import Instructions</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Download the CSV template to see the required format</li>
                <li>• Modules will be created automatically if they don't exist</li>
                <li>• Resources should be in JSON format (see template example)</li>
                <li>• Text content supports markdown formatting</li>
                <li>• Leave videoUrl empty if no video for the lesson</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Template Download */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg border border-gray-200 p-6 mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">CSV Template</h3>
              <p className="text-gray-600">Download the template to get started with the correct format</p>
            </div>
            <button
              onClick={downloadTemplate}
              className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiDownload} className="w-4 h-4" />
              <span>Download Template</span>
            </button>
          </div>
        </motion.div>

        {/* File Upload */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg border border-gray-200 p-6 mb-8"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload CSV File</h3>
          
          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              dragOver
                ? 'border-nutrition-400 bg-nutrition-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <SafeIcon icon={FiUpload} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              Drop your CSV file here, or click to browse
            </h4>
            <p className="text-gray-500 mb-4">Only CSV files are supported</p>
            
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              id="csv-upload"
              disabled={loading}
            />
            <label
              htmlFor="csv-upload"
              className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-6 py-3 rounded-lg font-medium transition-colors cursor-pointer inline-flex items-center space-x-2"
            >
              <SafeIcon icon={FiUpload} className="w-4 h-4" />
              <span>Select CSV File</span>
            </label>
          </div>
        </motion.div>

        {/* Progress */}
        {loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg border border-gray-200 p-6 mb-8"
          >
            <div className="flex items-center space-x-3 mb-4">
              <LoadingSpinner size="sm" />
              <h3 className="text-lg font-semibold text-gray-900">Importing Lessons...</h3>
            </div>
            
            {progress && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Progress</span>
                  <span>{progress.current} of {progress.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-nutrition-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
                {progress.currentItem && (
                  <p className="text-sm text-gray-600">
                    Processing: {progress.currentItem}
                  </p>
                )}
              </div>
            )}
          </motion.div>
        )}

        {/* Results */}
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Results</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-600">{results.successful}</p>
                <p className="text-sm text-green-800">Successful</p>
              </div>
              
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <SafeIcon icon={FiX} className="w-8 h-8 text-red-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-600">{results.failed}</p>
                <p className="text-sm text-red-800">Failed</p>
              </div>
              
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-600">{results.total}</p>
                <p className="text-sm text-blue-800">Total Processed</p>
              </div>
            </div>

            {results.errors && results.errors.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Errors:</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {results.errors.map((error, index) => (
                    <div key={index} className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                      <strong>Row {error.row}:</strong> {error.message}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BulkImport;